// The four-week planner (SCENARIOS D10, PLAN-2026-10-03-research-tools.md
// §8): a team's roster by position over the coming weeks, each cell its club's
// opponent and the matchup cue, a bye shaded, and under each position a count
// of who can play against how many it starts.
//
// The weeks ahead, by position, so a manager can plan for thin weeks. So it
// is grouped by position, and the count is what it is for.
//
// `GET /api/leagues/:id/teams/:teamId/plan?weeks=`, any member, any team.
// Facts only: nothing on it says whom to add or start.

import { z } from 'zod';
import { MatchupCue } from './matchup-cue';
import { PlayerSummary, TeamRef } from './views';

/**
 * One player's week:
 * - `game`: his club plays; the opponent, home or away, the kickoff, and the
 *   matchup cue (null before the opponent has played).
 * - `bye`: his club has no game that week.
 * - `tbd`: the week's games are not on the calendar yet, so nobody can say.
 * - `none`: he has no club (released), so he has no game.
 */
export const PlanCell = z.object({
  period: z.number().int().min(1),
  state: z.enum(['game', 'bye', 'tbd', 'none']),
  opponent: z.string().nullable(),
  home: z.boolean().nullable(),
  kickoff: z.string().nullable(),
  matchup: MatchupCue.nullable(),
});
export type PlanCell = z.infer<typeof PlanCell>;

export const PlanRow = z.object({
  player: PlayerSummary,
  /** On IR or the taxi squad: shown, and counted toward nobody's week. */
  reserve: z.boolean(),
  cells: z.array(PlanCell),
});
export type PlanRow = z.infer<typeof PlanRow>;

export const PlanGroup = z.object({
  /** A position, as the sport names it. */
  position: z.string(),
  /** How many starting spots name this position alone; 0 for one only a FLEX takes. */
  starting: z.number().int().min(0),
  rows: z.array(PlanRow),
  /**
   * Per week, how many in the group can play: a game that week, not ruled out,
   * not on IR. A game's designation (*Out*) rules him out of the first week
   * alone, the one his report is for; a standing one (injured reserve, a
   * suspension) of every week. Null for a week whose games are not on the
   * calendar yet.
   */
  able: z.array(z.object({ period: z.number().int().min(1), count: z.number().int().nullable() })),
});
export type PlanGroup = z.infer<typeof PlanGroup>;

export const PlanView = z.object({
  team: TeamRef,
  weeks: z.array(
    z.object({ period: z.number().int().min(1), label: z.string(), isCurrent: z.boolean() }),
  ),
  /** The positions the league starts, in its slot order, then any other the roster holds. */
  groups: z.array(PlanGroup),
});
export type PlanView = z.infer<typeof PlanView>;

/** How many weeks: four by default, as the coworker asked; up to eight. */
export const PlanQuery = z.object({
  weeks: z.coerce.number().int().min(1).max(8).default(4),
});
export type PlanQueryShape = z.infer<typeof PlanQuery>;
